import { Person } from '../../types';
import { isUsableExtractedRole } from '../../utils/extractedRole';

interface ThreeTierScores {
  /** Heuristic overlap with extracted skills / role / profile text (~50 baseline) */
  skillMatch: number;
  /** Batch LLM semantic score for this shortlist (0–100) */
  semanticMatch: number;
  teamFitBonus: number; // 0-50, cross-position value
}

interface AIResultCardProps {
  person: Person;
  matchScore?: number;
  scores?: ThreeTierScores;  // New three-tier scoring
  /** Extracted search role from criteria; when missing/invalid, Role AI fit + Overall are hidden */
  searchContextRole?: string | null;
  aiReasoning?: string;
  strengths?: string[];
  /** Per-search semantic rationale from batch scoring (not profile text). */
  searchInsight?: string;
  onClick?: () => void;
  onDragStart?: (person: Person) => void;
}

// Color helpers based on design spec
const getSkillMatchColor = (score: number): string => {
  if (score < 40) return 'text-red-500';
  if (score < 70) return 'text-yellow-400';
  return 'text-green-500';
};

const getSemanticMatchColor = (score: number): string => {
  if (score < 50) return 'text-gray-400';
  if (score < 80) return 'text-blue-500';
  return 'text-purple-500';
};

const getTeamFitColor = (score: number): string => {
  if (score < 15) return 'text-red-500';
  if (score < 35) return 'text-yellow-400';
  return 'text-green-500';
};

/** Combined rank score (blend + constraint penalties) — what results are sorted by */
const getOverallMatchColor = (score: number): string => {
  if (score < 40) return 'text-red-400';
  if (score < 70) return 'text-amber-300';
  return 'text-emerald-400';
};

export const AIResultCard = ({
  person,
  matchScore,
  scores,
  searchContextRole,
  aiReasoning,
  strengths,
  searchInsight,
  onClick,
  onDragStart
}: AIResultCardProps) => {
  const showRoleBasedScores = isUsableExtractedRole(searchContextRole);
  const matchNote = [searchInsight?.trim(), aiReasoning?.trim()].find(Boolean) || '';
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('person', JSON.stringify(person));
    onDragStart?.(person);
  };

  return (
    <div 
      data-testid="person-card"
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
      className="mb-4 p-4 bg-dark-card border border-dark-border rounded-lg hover:border-brand-blue transition-colors cursor-move"
    >
      <div className="grid grid-cols-3 gap-4">
        {/* Left: Person Summary Card */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-dark-text">{person.Name}</h3>
              <p className="text-sm text-dark-text-secondary">{person.Title}</p>
            </div>
            {/* Three-tier scores or fallback to single score */}
            {scores ? (
              <div className="flex flex-col items-end gap-0.5 text-xs font-bold leading-tight">
                <span
                  className={getSkillMatchColor(scores.skillMatch)}
                  title="Heuristic overlap with extracted skills and profile text (not a literal text-match %)."
                >
                  🧩 Skills {Math.round(scores.skillMatch)}%
                </span>
                {showRoleBasedScores && (
                  <span
                    className={getSemanticMatchColor(scores.semanticMatch)}
                    title="LLM fit score for this query over the current shortlist."
                  >
                    ✨ Role AI fit {Math.round(scores.semanticMatch)}%
                  </span>
                )}
                {showRoleBasedScores && matchScore != null && Number.isFinite(matchScore) && (
                  <span
                    className={getOverallMatchColor(matchScore)}
                    title="Combined rank used for sorting (heuristic + LLM fit, with constraint penalties)."
                  >
                    ⭐ Overall {Math.round(matchScore)}%
                  </span>
                )}
                {scores.teamFitBonus > 0 && (
                  <span className={getTeamFitColor(scores.teamFitBonus)} title="Cross-position team fit bonus">
                    👥+{scores.teamFitBonus}%
                  </span>
                )}
              </div>
            ) : matchScore !== undefined && showRoleBasedScores ? (
              <div className="bg-brand-gradient text-white text-xs font-bold px-2 py-1 rounded-full">
                ⭐ {matchScore}%
              </div>
            ) : null}
          </div>
          
          <div className="text-xs text-dark-text-secondary space-y-1">
            <p>📍 {person.PrimaryLocation || 'N/A'}</p>
            <p>👥 {person.Team || 'N/A'}</p>
          </div>

          <div className="text-xs mt-2">
            <p className="text-dark-text-secondary">Skills:</p>
            <p className="text-dark-text line-clamp-2">{person.Expertise || 'N/A'}</p>
          </div>
        </div>

        {/* Middle: single narrative (LLM insight preferred over heuristic strings) */}
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-brand-blue">Match note</h4>
          {matchNote ? (
            <p className="text-sm text-dark-text">{matchNote}</p>
          ) : (
            <p className="text-sm text-dark-text-secondary italic">No short explanation for this result.</p>
          )}
          
          {strengths && strengths.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {strengths.slice(0, 3).map((strength, idx) => (
                <span 
                  key={idx} 
                  className="text-xs bg-brand-blue/20 text-brand-blue px-2 py-0.5 rounded"
                >
                  {strength}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right: Job Responsibilities + search-specific insight + profile interests */}
        <div className="flex flex-col gap-2 min-h-0">
          <h4 className="text-xs font-semibold text-dark-text">📋 Responsibilities</h4>
          <p className="text-sm text-dark-text line-clamp-3">
            {person.JobResponsibilities || 'See full profile for details'}
          </p>
          {searchInsight?.trim() && searchInsight.trim() !== matchNote && (
            <div className="mt-1 pt-2 border-t border-dark-border/60">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-cyan-300/90 mb-0.5">
                For this search
              </h4>
              <p
                className="text-xs text-dark-text leading-snug line-clamp-3"
                title={searchInsight}
              >
                ✨ {searchInsight}
              </p>
            </div>
          )}
          <div
            className={`mt-auto pt-2 ${
              searchInsight?.trim() && searchInsight.trim() !== matchNote ? 'border-t border-dark-border/40' : ''
            }`}
          >
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-dark-text-secondary mb-0.5">
              From profile
            </h4>
            <p
              className="text-xs text-dark-text-secondary line-clamp-3"
              title={person.ProfessionalInterests || undefined}
            >
              💡 {person.ProfessionalInterests || 'Career interests not listed'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

